import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { loginBodySchema, type LoginBody } from '@amai/contracts';
import { Button } from '@/components/Button';
import { Field } from '@/components/FormSection';
import { useAuth } from '@/lib/auth-context';
import { errorMessage } from '@/lib/errors';

/**
 * Entrada da plataforma: logo, dois campos, um botão.
 *
 * Era uma landing page. Metade esquerda com slogan em duas cores ("Simples
 * para operar. Poderoso para gerenciar."), círculos concêntricos de enfeite,
 * rótulos em caixa alta espaçada, três selos de "diferencial" com ícone em
 * círculo, cadeado num quadradinho em cima do formulário e um "Seus dados
 * estão protegidos" com escudo embaixo. Tudo isso é o repertório de modelo
 * gerado — e quem chega aqui é funcionário do parque, que já sabe o que é o
 * sistema e só quer entrar. Ferramenta de trabalho séria não se vende no
 * login.
 *
 * O erro de credencial é sempre a mesma frase, venha de e-mail inexistente ou
 * senha errada — a API já responde assim de propósito, e a tela não inventa
 * uma distinção que a API não faz.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [failure, setFailure] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [senhaPerdida, setSenhaPerdida] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginBody>({
    resolver: zodResolver(loginBodySchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await signIn(values.email, values.password);
      await navigate({ to: '/' });
    } catch (error) {
      setFailure(errorMessage(error));
    }
  });

  return (
    <main className="login">
      <form className="login__form" onSubmit={onSubmit} noValidate>
        <img className="login__logo" src="/logo-amai.png" alt="AMAI Park" />
        <h1 className="login__title">Entrar no AMAI OPS</h1>

        {failure && (
          <div className="login__alert" role="alert">
            <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{failure}</span>
          </div>
        )}

        <Field label="E-mail" error={errors.email?.message} htmlFor="email">
          <input
            id="email"
            className="input"
            type="email"
            inputMode="email"
            placeholder="seu@amaipark.com"
            autoComplete="username"
            autoFocus
            aria-invalid={Boolean(errors.email)}
            {...register('email')}
          />
        </Field>

        <Field label="Senha" error={errors.password?.message} htmlFor="password">
          <div className="login__senha">
            <input
              id="password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              aria-invalid={Boolean(errors.password)}
              {...register('password')}
            />
            <button
              type="button"
              className="login__peek"
              aria-label={showPassword ? 'Ocultar a senha' : 'Mostrar a senha'}
              aria-pressed={showPassword}
              title={showPassword ? 'Ocultar a senha' : 'Mostrar a senha'}
              onClick={() => setShowPassword((value) => !value)}
            >
              {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
            </button>
          </div>
        </Field>

        <Button type="submit" variant="primary" className="login__submit" loading={isSubmitting}>
          {isSubmitting ? 'Entrando…' : 'Entrar'}
        </Button>

        <button
          type="button"
          className="login__forgot"
          aria-expanded={senhaPerdida}
          onClick={() => setSenhaPerdida((value) => !value)}
        >
          Esqueci minha senha
        </button>

        {/* Não existe (ainda) recuperação por e-mail nesta plataforma. Um
            link que abrisse uma tela vazia seria pior do que dizer a
            verdade: quem reabre senha aqui é o administrador. */}
        {senhaPerdida && (
          <p className="login__forgot-help" role="status">
            A senha é redefinida pelo administrador, em Configurações › Usuários.
          </p>
        )}
      </form>
    </main>
  );
}
